import { FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { ID, isNotEmpty } from '../../../../../_dashboard/helpers'
import { Facility } from '../core/_models'
import clsx from 'clsx'
import { useListView } from '../core/ListViewProvider'
import { DataLoading } from '../../../../../_dashboard/partials/components/loading/DataLoading'
import { useQueryResponse } from '../core/QueryResponseProvider'
import { createOne, getAll, getOneByIdbylang, updateOne } from '../../../../services/common_requests'
import { useIntl } from 'react-intl'
import ImageUploader from '../../../../../_dashboard/partials/widgets/upload/ImageUploader'
import { images } from '../../../hotel-management/hotel-details/core/_models'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { useQueryRequest } from '../core/QueryRequestProvider'

type Props = {
  isFacilityLoading: boolean
  Facility: Facility
}

const editFacilitySchema = Yup.object().shape({
  title: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(50, 'Maximum 50 symbols')
    .required('Title is required'),
})

const FacilityEditModalForm: FC<Props> = ({ Facility, isFacilityLoading }) => {
  const [languages, setLanguages] = useState<{ code: string; name: string }[]>([])
  const [selectedLanguage, setSelectedLanguage] = useState('en')
  const { state } = useQueryRequest()
  const [query, setQuery] = useState<any>(state)

  const { itemIdForUpdate, setItemIdForUpdate } = useListView()
  const { refetchData } = useQueryResponse()
  const intl = useIntl()
  const [FacilityForEdit] = useState<Facility>({
    ...Facility,
    translations: Facility.translations ?? [{ language: 'en', name: '' }],
  })
  const [iconPreview, setIconPreview] = useState<string | null>(null)
  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }
  const [images, setImages] = useState<images[]>([])
  const LANGUAGE_NAMES: { [key: string]: string } = {
    en: useIntl().formatMessage({ id: 'COMMON.ENGLISH_LANGUAGE' }),
    fr: useIntl().formatMessage({ id: 'COMMON.FRENSH_LANGUAGE' }),
    es: useIntl().formatMessage({ id: 'COMMON.ESPANGOL_LANGUAGE' }),
    ar: useIntl().formatMessage({ id: 'COMMON.ARABIC_LANGUAGE' }),
  }
  useEffect(() => {
    getAll(query, '/languages').then((data) => {
      if (data && Array.isArray(data)) {
        const formattedLanguages = data.map((code: string) => ({
          code,
          name: LANGUAGE_NAMES[code] || code.toUpperCase(),
        }))
        setLanguages(formattedLanguages)
      }
    })
  }, [query])
  useEffect(() => {
    if (itemIdForUpdate && selectedLanguage) {
      getOneByIdbylang(`/facilities/${itemIdForUpdate}?language=${selectedLanguage}`)
        .then((data) => {
          if (data && data.translations) {
            // Mettre à jour toutes les traductions
            formik.setValues({
              ...formik.values,
              translations: data.translations.map((t: any) => ({
                ...t,
                _id: t._id,
              })),
            })
          }
        })
        .catch((error) => console.error('Error fetching translation:', error))
    }
  }, [selectedLanguage, itemIdForUpdate])

  const handleImageAdded = (newImage: { url: string; _id: string }) => {
    setImages((prevImages) => [
      ...prevImages,
      {
        ...newImage,
        seoUrl: '',
        _id: newImage._id as unknown as ID,
      },
    ])
  }
  const [uploadedIcon, setUploadedIcon] = useState<File | null>(null)
  const handleIconChange = (file: File | null) => {
    if (file) {
      setUploadedIcon(file)
      setIconPreview(URL.createObjectURL(file))
    }
  }
  const formik = useFormik({
    initialValues: FacilityForEdit,
    validationSchema: editFacilitySchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      if (isNotEmpty(itemIdForUpdate)) {
        const selectedTranslation = values.translations.find((t) => t.language === selectedLanguage)

        if (!selectedTranslation || !selectedTranslation._id) {
          toast.error(intl.formatMessage({ id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND' }))
          return
        }
        const formData = new FormData()

        if (uploadedIcon) {
          formData.append('icon', uploadedIcon)
        }

        formData.append('title', selectedTranslation.title)

        await updateOne(formData, `/facilities/translation/${selectedTranslation._id}`).then(
          (response) => {
            if (response?.payload?.code === 2021) {
              toast.success(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.SUCCESS.FACILITY_UPDATED_SUCCESSFULLY',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
              cancel(true)
              refetchData()
            } else if (response?.payload?.code === 4401) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ID_NOT_EXIST',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            } else if (response?.payload?.code === 9100) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ID_NOT_FOUND',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            } else {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_FACILITY',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            }
          }
        )
      } else {
        const payload = {
          title: values.translations[0].title,
          translations: values.translations.map((translation) => ({
            language: translation.language || 'en',
            title: translation.title,
          })),
        }
        try {
          await createOne(payload, '/facilities').then((response) => {
            if (response?.payload?.code === 2021) {
              toast.success(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.SUCCESS.THEMES_ADDED_SUCCESSFULLY',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
              cancel(true)
            } else if (response?.payload?.code === 4400) {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.NAME_ALREADY_EXIST',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            } else {
              toast.error(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.ERROR_ADDING_THEME',
                }),
                {
                  position: 'top-right',
                  autoClose: 4000,
                }
              )
            }
          })
        } catch (ex) {
          console.error(ex)
        } finally {
          setSubmitting(true)
          cancel(true)
        }
      }
    },
  })
  const selectedTranslation = formik.values.translations.find(
    (t) => t.language === selectedLanguage
  )

  return (
    <>
      <form
        id='kt_modal_add_facility_form'
        className='form'
        onSubmit={formik.handleSubmit}
        noValidate
      >
        {/* begin::Scroll */}
        <div
          className='d-flex flex-column scroll-y me-n15 pe-6'
          id='kt_modal_add_facilities_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_facilities_header'
          data-kt-scroll-wrappers='#kt_modal_add_facilities_scroll'
          data-kt-scroll-offset='300px'
        >
          {/* begin::Input group */}
          <div className='fv-row mb-4'>
            {/* begin::Label */}
            <label className='d-block fw-bold fs-6 mb-5'>
              {' '}
              {intl.formatMessage({ id: 'COLUMNS.ICON' })}
            </label>
            {/* end::Label */}

            {!itemIdForUpdate ? (
              <ImageUploader
                onImageUpload={handleIconChange}
                uploadEndpoint={`/hotels/${itemIdForUpdate}`}
                onImageAdded={handleImageAdded}
              />
            ) : (
              <div
                className='image-input image-input-outline'
                data-kt-image-input='true'
                style={{
                  backgroundImage: `url('${iconPreview || (process.env.REACT_APP_API_URL_IMAGE || '') + Facility?.icon?.url
                    }')`,
                }}
              >
                <div className='image-input-wrapper w-125px h-125px'></div>
                <label
                  className='btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow'
                  data-kt-image-input-action='change'
                  data-bs-toggle='tooltip'
                  title='Change avatar'
                >
                  <i className='bi bi-pencil-fill fs-7'></i>
                  <input
                    type='file'
                    name='icon'
                    accept='.png, .jpg, .jpeg'
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleIconChange(e.target.files[0])
                      }
                    }}
                  />
                  <input type='hidden' name='avatar_remove' />
                </label>
              </div>
            )}

            {/* end::Image input */}

            {/* begin::Hint */}
            <div className='form-text'> {intl.formatMessage({ id: 'COMMUN.ALLOWED_FILE' })}</div>
            {/* end::Hint */}
          </div>
          {/* end::Input group */}
          <div className='fv-row mb-4'>
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.LANGUAGE' })}
            </label>
            {formik.values.translations.map((translation, index) => (
              <div key={index}>
                <select
                  id={`translations[${index}].language`}
                  name={`translations[${index}].language`}
                  onChange={(e) => {
                    formik.handleChange(e)
                    setSelectedLanguage(e.target.value) // Met à jour la langue sélectionnée
                  }}
                  value={translation.language}
                  className='form-select'
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {formik.errors.translations &&
                  (formik.errors.translations[index] as { language?: string })?.language && (
                    <div>{(formik.errors.translations[index] as { language: string }).language}</div>
                  )}
              </div>
            ))}
          </div>
          {/* begin::Input group */}
          <div className='fv-row mb-4'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({ id: 'COLUMNS.NAME' })}
            </label>
            {/* end::Label */}

            {/* begin::Input */}
            <input
              placeholder={intl.formatMessage({ id: 'COLUMNS.NAME' })}
              value={selectedTranslation?.title || ''}
              onChange={(e) => {
                const index = formik.values.translations.findIndex(
                  (t) => t.language === selectedLanguage
                )
                if (index !== -1) {
                  formik.setFieldValue(`translations[${index}].title`, e.target.value)
                }
              }}
              type='text'
              name='title'
              className={clsx(
                'form-control mb-3 mb-lg-0',
                {
                  'is-invalid':
                    formik.touched.translations?.[0]?.title &&
                    (formik.errors.translations?.[0] as { title?: string })?.title,
                },
                {
                  'is-valid':
                    formik.touched.translations?.[0]?.title &&
                    !(formik.errors.translations?.[0] as { title?: string })?.title,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isFacilityLoading}
            />

            {/* end::Input */}
          </div>
          {/* end::Input group */}
        </div>
        {/* end::Scroll */}

        {/* begin::Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-facilities-modal-action='cancel'
            disabled={formik.isSubmitting || isFacilityLoading}
          >
            {intl.formatMessage({ id: 'HEADER.RESET' })}
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-facilities-modal-action='submit'
            disabled={
              isFacilityLoading || formik.isSubmitting || !formik.isValid || !formik.touched
            }
          >
            <span className='indicator-label'> {intl.formatMessage({ id: 'HEADER.SAVE' })}</span>
            {(formik.isSubmitting || isFacilityLoading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Actions */}
      </form>
      {(formik.isSubmitting || isFacilityLoading) && <DataLoading />}
    </>
  )
}

export { FacilityEditModalForm }
